import Checkbox from '@/components/Checkbox/Checkbox';
import inputStyles from '@/components/Input/Input.module.css';
import SortIcon from '@/components/Tables/SortIcon';
import useFranchiseeAlias from '@/utils/useFranchiseeAlias/useFranchiseeAlias';
import Tooltip from '@mui/material/Tooltip';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { Grid, IconButton } from '@mui/material';
import {
  ColumnDef,
  ColumnFiltersState,
  ExpandedState,
  Row,
  SortingState,
  Table as TableType,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
  Cell,
  CellContext,
} from '@tanstack/react-table';
import classNames from 'classnames';
import {
  KeyboardEvent,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  Children,
} from 'react';
import { ChevronDown, ChevronUp } from 'react-feather';
import styles from './FZMenuTable.module.css';
import { TableCardProps } from './FZMenuTable.types';
import { StoreProduct, Variant } from '@/types/menus.types';
import CircleOutlinedIcon from '@mui/icons-material/CircleOutlined';
import CircleIcon from '@mui/icons-material/Circle';
import { useCurrentUser } from '@/services/user/user.service';
import { UserRoles, UserTypes } from '@/types/auth.types';
import { useTranslation } from 'react-i18next';
import { tooltipProps, TooltipContent } from './TooltipHelper';

interface ExpandableMenuAreaProps {
  cell: Cell<StoreProduct, unknown>;
  variant?: Variant;
}
export const ExpandableMenuArea = ({ cell, variant }: ExpandableMenuAreaProps) => {
  const storeGroupList = variant?.storeGroups.map((storeGroup) => storeGroup.storeGroupName);
  const loadingVariant = !variant;
  const variantLoading =
    loadingVariant && cell.column.columnDef.id !== 'select' && cell.column.columnDef.header !== 'PRODUCT';

  return (
    <td key={cell.id} className={styles.cell} style={{ borderTop: '1px solid #DBDBDB' }}>
      {cell.column.columnDef.header === 'PRODUCT' && (
        <div>
          <span className={styles.productOnMenu}> Product on menu</span>{' '}
        </div>
      )}

      {variantLoading ? (
        <div className={styles.availableProduct}>--</div>
      ) : (
        <>
          {storeGroupList?.includes(cell.column.id) &&
            variant?.storeGroups.filter((sg) => sg.storeGroupName === cell.column.id)[0]?.storesWithVariant ===
              'ALL' && (
              <div className={styles.availableProduct}>
                <CircleIcon />
              </div>
            )}
          {storeGroupList?.includes(cell.column.id) &&
            variant?.storeGroups.filter((sg) => sg.storeGroupName === cell.column.id)[0]?.storesWithVariant ===
              'SOME' && (
              <div className={styles.availableProduct}>
                <CircleOutlinedIcon />
              </div>
            )}
          {storeGroupList?.includes(cell.column.id) &&
            variant?.storeGroups.filter((sg) => sg.storeGroupName === cell.column.id)[0]?.storesWithVariant ===
              'NONE' && <div className={styles.availableProduct}>--</div>}
        </>
      )}
    </td>
  );
};

const FZMenuTable = <TData extends object>({
  data = [],
  columns,
  enableFilters = false,
  enableMultiRowSelection = true,
  noRowsMessage = '',
  title,
  selectedRows,
  onRowSelectionChange,
  isLoading,
  accessTableApi,
  showPagination = false,
  meta,
  onHandleSetVariantId,
  variant,
  isFranchiseeEditAndFreeze,
  changeHeaderSize,
}: TableCardProps<StoreProduct>) => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [rowSelection, setRowSelection] = useState({});
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [variantList, setVariantList] = useState<Variant[]>([]);

  const id = useId();
  const { t } = useTranslation();
  const [headerIsSticky, setHeaderIsSticky] = useState(false);
  const { alias } = useFranchiseeAlias();
  const { data: dataUser } = useCurrentUser(true);

  const isUserViewOnlyAccess = dataUser?.data.role === UserRoles.VIEW;

  useEffect(() => {
    const stickyHeaderHandler = () => {
      if (window.scrollY > 210) {
        setHeaderIsSticky(true);
      } else {
        setHeaderIsSticky(false);
      }
    };

    window.addEventListener('scroll', stickyHeaderHandler);

    return () => {
      window.removeEventListener('scroll', stickyHeaderHandler);
    };
  }, []);

  useEffect(() => {
    if (variant) {
      const variants = [...variantList];
      variants.push(variant);
      setVariantList(variants);
    }
  }, [variant]);

  // if we have an onRowSelectionChange prop, we assume selection state is being managed externally (via the selectedRows prop)
  const getRowSelectionHandler = () => {
    if (onRowSelectionChange) return onRowSelectionChange;
    return setRowSelection;
  };

  // TODO: to be implemented
  const onHandleGetVariants = useCallback((row: Row<StoreProduct>) => {
    onHandleSetVariantId(row.original.variantId || '');
    row.toggleExpanded();
  }, []);

  // if we receive selectedRows from props, update our internal state
  useEffect(() => {
    if (selectedRows) {
      setRowSelection(selectedRows);
    }
  }, [selectedRows]);

  const LoadingSkeleton = useCallback(
    ({ column }: CellContext<TData, StoreProduct>) =>
      column.id === 'id' ? <div /> : <div className={styles.skeleton} data-testid="loading-skeleton-cell" />,
    [],
  );

  const renderCheckboxCell = useCallback(
    (params: { row: Row<StoreProduct> }) => {
      if (params.row.original.priceLock && params.row.original.mandatoryProductLock) {
        return (
          <Grid
            container
            direction="row"
            justifyContent="left"
            alignItems="flex-start"
            width="80px"
            sx={{
              maxWidth: '80px',
            }}
          >
            <Grid item xs={6} paddingTop={0.5}>
              <IconButton
                size="small"
                data-testid="edit-icon"
                onClick={() => onHandleGetVariants(params.row)}
                sx={{
                  fontSize: '24px',
                }}
              >
                {params.row.getIsExpanded() ? <ChevronUp /> : <ChevronDown />}
              </IconButton>
            </Grid>
            <Grid item xs={6} alignContent="center" alignItems="flex-start" padding={0.5}>
              <Tooltip {...tooltipProps} title={<TooltipContent text={t('menuDetails.priceAndMandatoryLockMsg')} />}>
                <LockOutlinedIcon
                  style={{ color: '#266DCC' }}
                  data-testid="locked-icon"
                  sx={{
                    width: '25px',
                    height: '25px',
                    marginRight: '5px',
                    marginBottom: '-2px',
                  }}
                />
              </Tooltip>
            </Grid>
          </Grid>
        );
      }
      return (
        <Grid
          container
          direction="row"
          justifyContent="left"
          alignItems="flex-start"
          width={isFranchiseeEditAndFreeze ? '25px' : '80px'}
          sx={{
            maxWidth: '80px',
          }}
        >
          <Grid item xs={6} paddingTop={0.5}>
            <IconButton
              size="small"
              data-testid="row-expansion-icon"
              onClick={() => onHandleGetVariants(params.row)}
              sx={{
                fontSize: '24px',
              }}
            >
              {params.row.getIsExpanded() ? <ChevronUp /> : <ChevronDown />}
            </IconButton>
          </Grid>
          {!isFranchiseeEditAndFreeze && !isUserViewOnlyAccess && (
            <Grid item xs={6} alignContent="center" alignItems="flex-start" padding={0.5}>
              <Checkbox
                label=""
                data-testid="checkbox-icon"
                aria-label="select row"
                id={`${id}-row-${params.row.id}-checkbox`}
                checked={params.row.getIsSelected()}
                onChange={params.row.getToggleSelectedHandler()}
                disabled={!params.row.getCanSelect()}
              />
            </Grid>
          )}
        </Grid>
      );
    },
    [id],
  );

  const renderHeaderCell = useCallback(
    (params: { table: TableType<unknown> }) => {
      if (!isFranchiseeEditAndFreeze && !isUserViewOnlyAccess) {
        return (
          <div>
            <div className={styles.headerCheckbox}>
              <Checkbox
                label=""
                data-testid="select-all-menu-products"
                aria-label="select all rows"
                id={`${id}-header-checkbox`}
                checked={params.table.getIsAllRowsSelected()}
                onChange={params.table.getToggleAllRowsSelectedHandler()}
              />
            </div>
          </div>
        );
      }

      return null;
    },
    [id, isFranchiseeEditAndFreeze, isUserViewOnlyAccess],
  );

  const columnsWithRowSelection = useMemo(
    () => [
      {
        id: 'select',
        header: renderHeaderCell,
        cell: renderCheckboxCell,
        columnClassname: isFranchiseeEditAndFreeze && isUserViewOnlyAccess ? 'headerWithoutCheckbox' : 'checkboxHeader',
      } as ColumnDef<StoreProduct, any>,
      ...columns,
    ],
    [columns, isFranchiseeEditAndFreeze, isUserViewOnlyAccess, renderCheckboxCell, renderHeaderCell],
  );

  const getPrinceByOrderAndSort = (item: StoreProduct, sort: string, columnId: string) => {
    const chosenPriceRange = item.storeGroups
      .find((sg) => sg.storeGroupName === columnId)
      ?.chosenPriceRange?.split('-') as unknown as string[];

    if (!chosenPriceRange) return '';

    if (chosenPriceRange.length === 1) return Number(chosenPriceRange[0]);

    if (sort === 'asc') return Number(chosenPriceRange[0]);

    if (sort === 'desc') return Number(chosenPriceRange[1]);

    return Number(chosenPriceRange[0]);
  };

  const getChosenPriceRangeBySort = (rowA: Row<StoreProduct>, rowB: Row<StoreProduct>, columnId: string) => {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    const tableSortingState = table.getState().sorting;
    const tableSorting = tableSortingState.find((sort) => sort.id === columnId);
    const currentSort = tableSorting?.desc ? 'desc' : 'asc';
    const priceRangeA = getPrinceByOrderAndSort(rowA.original, currentSort, columnId);
    const priceRangeB = getPrinceByOrderAndSort(rowB.original, currentSort, columnId);

    if (Number.isNaN(priceRangeA) || Number.isNaN(priceRangeB)) return -1;

    const isEqual = priceRangeA === priceRangeB;
    if (isEqual) return 0;

    return priceRangeA > priceRangeB ? 1 : -1;
  };

  const sortProductsByProductNameAndProductDescription = (rowA: Row<StoreProduct>, rowB: Row<StoreProduct>) => {
    if (rowA.original.productName < rowB.original.productName) return -1;
    if (rowA.original.productName > rowB.original.productName) return 1;
    if (rowA.original.productName === rowB.original.productName) {
      if (rowA.original.productDescription < rowB.original.productDescription) return -1;
      if (rowA.original.productDescription > rowB.original.productDescription) return 1;
    }
    return 0;
  };

  const table = useReactTable({
    data,
    columns: columnsWithRowSelection,
    getCoreRowModel: getCoreRowModel(),
    enableFilters,
    enableMultiRowSelection,
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      sorting,
      columnFilters,
      rowSelection,
      expanded,
    },
    onExpandedChange: setExpanded,
    getExpandedRowModel: getExpandedRowModel(),
    onSortingChange: setSorting,
    onRowSelectionChange: getRowSelectionHandler(),
    getSortedRowModel: getSortedRowModel(),
    meta,
    debugTable: false,
    sortingFns: {
      sortProductByPrice: getChosenPriceRangeBySort,
      sortProduct: sortProductsByProductNameAndProductDescription,
    },
  });

  useEffect(() => {
    if (accessTableApi) accessTableApi(table);
  }, [table, accessTableApi]);

  const getAriaSort = (sortStatus: 'asc' | 'desc' | false) => {
    switch (sortStatus) {
      case 'asc':
        return 'ascending';
      case 'desc':
        return 'descending';
      default:
        return 'none';
    }
  };

  const filteredRowCount = table.getFilteredRowModel().rows.length;

  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [hasHorizontalScrollbar, setHasHorizontalScrollbar] = useState(false);
  const [hasVerticalScrollbar, setHasVerticalScrollbar] = useState(false);

  const checkForScrollbar = useCallback(() => {
    if (!tableContainerRef.current) return;
    if (tableContainerRef.current.clientWidth === tableContainerRef.current.scrollWidth) {
      setHasHorizontalScrollbar(false);
    }
    if (tableContainerRef.current.clientWidth < tableContainerRef.current.scrollWidth) {
      setHasHorizontalScrollbar(true);
    }
    if (tableContainerRef.current.clientHeight === tableContainerRef.current.scrollHeight) {
      setHasVerticalScrollbar(false);
    }
    if (tableContainerRef.current.clientHeight < tableContainerRef.current.scrollHeight) {
      setHasVerticalScrollbar(true);
    }
  }, []);

  useLayoutEffect(() => {
    checkForScrollbar();
  }, [isLoading, checkForScrollbar]);

  useEffect(() => {
    window.addEventListener('resize', checkForScrollbar);
    return () => window.removeEventListener('resize', checkForScrollbar);
  }, [checkForScrollbar]);

  function calculateMinWidth(columnsInternal: any[]): string {
    let minWidth: string;
    if (columnsInternal.length <= 5) {
      minWidth = '350px';
    } else {
      minWidth = `${columnsInternal.length * 2 + 280}px`;
    }
    return minWidth;
  }
  function calculateMaxTableSize(columnsInternal: any[]) {
    const maxTableSize = columnsInternal.length <= 7 ? '' : `${(columnsInternal.length - 5) * 5 + 100}vw`;
    changeHeaderSize(maxTableSize);
    return maxTableSize;
  }

  return (
    <div
      style={{ minWidth: calculateMaxTableSize(columns) }}
      ref={tableContainerRef}
      className={classNames(styles.tableContainer, {
        [styles.noTitle]: title === undefined,
        // [styles.loading]: isLoading,
        [styles.tableFilled]: showPagination,
        [styles.withVerticalScrollBar]: hasVerticalScrollbar,
      })}
    >
      <table className={styles.table} style={{ minWidth: '-webkit-fill-available' }}>
        <thead className={classNames({ [styles.whenSticky]: headerIsSticky, [styles.withAlias]: alias })}>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header, index) => (
                <th
                  data-testid={`table-header-${header.id}`}
                  key={header.id}
                  className={classNames(
                    styles.columnHeader,
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    //@ts-ignore
                    styles[header.column.columnDef.columnClassname],
                  )}
                  aria-sort={getAriaSort(header.column.getIsSorted())}
                >
                  <div
                    data-testid={`table-sort-button-${header.id}`}
                    className={styles.columnHeaderTextAndIcon}
                    tabIndex={header.column.columnDef.enableSorting ? 0 : undefined}
                    aria-label={`sort column ${
                      typeof header.column.columnDef.header === 'string' ? header.column.columnDef.header : ''
                    }`}
                    onClick={
                      header.column.columnDef.enableSorting ? header.column.getToggleSortingHandler() : undefined
                    }
                    onTouchStart={() => null}
                    role="button"
                    onKeyDown={(event: KeyboardEvent) => {
                      switch (event.key) {
                        case ' ':
                        case 'Enter':
                          header.column.getToggleSortingHandler();
                          break;
                        default:
                          break;
                      }
                    }}
                  >
                    <span className={styles.columnHeaderText}>
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    </span>
                    {header.column.columnDef.enableSorting && <SortIcon />}
                  </div>
                  {header.column.getCanFilter() ? (
                    <input
                      disabled={header.column.columnDef.meta?.disableFilter}
                      onChange={(event) => header.column.setFilterValue(event.target.value)}
                      value={(header.column.getFilterValue() as string) ?? ''}
                      className={classNames(inputStyles.input, styles.headerFilter)}
                      aria-label={`${header.column.columnDef.header} filter input`}
                      id={`${header.id}filterInput`}
                    />
                  ) : null}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {Children.toArray(
            table.getRowModel().rows.map((row, index) => (
              <>
                <tr
                  key={row.id}
                  data-testid="row-data"
                  className={classNames(styles.row, {
                    [styles.even]: index % 2 === 0,
                    [styles.selectedRow]: row.getIsSelected(),
                  })}
                >
                  {row.getVisibleCells().map((cell) =>
                    cell?.column?.columnDef?.header !== 'PRODUCT' ||
                    typeof cell?.column?.columnDef?.header !== 'string' ? (
                      <td key={cell.id} className={styles.cell}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ) : (
                      <td
                        key={cell.id}
                        className={styles.cell}
                        style={{ minWidth: calculateMinWidth(columns), color: 'blue' }}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ),
                  )}
                </tr>

                {row.getIsExpanded() && (
                  //Displays available product row after expansion
                  <tr
                    key={row.id}
                    className={classNames(styles.row, {
                      [styles.even]: index % 2 === 0,
                      [styles.selectedRow]: row.getIsSelected(),
                    })}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <ExpandableMenuArea
                        key={cell.id}
                        cell={cell}
                        variant={variantList.find((pVariant) => pVariant.variantId === row.original.variantId)}
                      />
                    ))}
                  </tr>
                )}
              </>
            )),
          )}
        </tbody>

        {!isLoading && filteredRowCount === 0 && <caption className={styles.noRowsMessage}>{noRowsMessage}</caption>}
      </table>
    </div>
  );
};

export default FZMenuTable;
